import type { OnboardingDataItemType } from "@memorang/constants/config/schema";
import { getCalendarDaysUntil } from "@memorang/helpers/date";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { List, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import { useOnboarding } from "./OnboardingProvider";
import { ChangeLabel } from "./components/ChangeLabel";
import { OnboardingBox } from "./components/OnboardingBox";
import { getRecommendedPlanId, resolveI18n } from "./helpers";

type Props = {
	title: string;
	description?: string;
	data?: OnboardingDataItemType[];
};

type PlanItemProps = {
	item: OnboardingDataItemType;
	isSelected: boolean;
	isRecommended: boolean;
	onPress: () => void;
	locale: string;
};

const PlanItem = ({
	item,
	isSelected,
	isRecommended,
	onPress,
	locale,
}: PlanItemProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const title = resolveI18n(item.titlei18n, locale);
	const description = resolveI18n(item.descriptioni18n, locale);

	return (
		<List.Item
			onPress={onPress}
			style={{
				borderWidth: 1,
				borderColor: isSelected
					? theme.colors.secondary
					: theme.colors.surfaceVariant,
				borderRadius: theme.roundness * 2,
				backgroundColor: isSelected
					? theme.colors.elevation.level1
					: theme.colors.surface,
				padding: 16,
			}}
			contentStyle={{ gap: 1 }}
			left={() => (
				<Box
					style={{
						width: 36,
						height: 36,
						borderRadius: 99,
						backgroundColor: isSelected
							? theme.colors.elevation.level5
							: theme.colors.background,
						justifyContent: "center",
						alignItems: "center",
						alignSelf: "center",
					}}
				>
					<AppIcon
						name={(item.icon ?? "help-circle-outline") as never}
						size={24}
						color={isSelected ? theme.colors.secondary : theme.colors.onSurface}
					/>
				</Box>
			)}
			title={title}
			titleStyle={{ color: theme.colors.onSurface }}
			description={description}
			descriptionStyle={{ color: theme.colors.onSurfaceVariant }}
			right={
				isRecommended
					? () => (
							<Box
								style={{
									borderRadius: theme.roundness,
									backgroundColor: theme.colors.elevation.level5,
									paddingHorizontal: 8,
									paddingVertical: 4,
									alignSelf: "center",
								}}
							>
								<Text
									variant="labelSmall"
									style={{ color: theme.colors.onSurface }}
								>
									{t("onboarding.steps.plan.recommended")}
								</Text>
							</Box>
						)
					: undefined
			}
		/>
	);
};

export const OnboardingPlanScreen = ({
	title,
	description,
	data = [],
}: Props) => {
	const theme = useAppTheme();
	const { selectedPlanId, onSelectPlan, selectedExamDate, totalItemCount } =
		useOnboarding();
	const { i18n } = useTranslation();
	const locale = i18n.language;

	const daysUntilExam = getCalendarDaysUntil(selectedExamDate);
	const recommendedPlanId =
		daysUntilExam != null && totalItemCount != null
			? getRecommendedPlanId({
					daysUntilExam,
					totalItemCount,
					options: data,
				})
			: undefined;

	useEffect(() => {
		if (selectedPlanId == null && recommendedPlanId != null) {
			onSelectPlan(recommendedPlanId);
		}
	}, [selectedPlanId, recommendedPlanId, onSelectPlan]);

	return (
		<Box gap={10}>
			<OnboardingBox>
				<Box gap={14}>
					<Text variant="titleLarge">{title}</Text>
					{description ? (
						<Text
							variant="bodyMedium"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{description}
						</Text>
					) : null}
				</Box>
				<Box gap={8}>
					{data.map((item) => {
						const id = item.type ?? resolveI18n(item.titlei18n, "en-US") ?? "";
						return (
							<PlanItem
								key={id}
								item={item}
								locale={locale}
								isSelected={selectedPlanId === id}
								isRecommended={recommendedPlanId === id}
								onPress={() => onSelectPlan(id)}
							/>
						);
					})}
				</Box>
			</OnboardingBox>
			<ChangeLabel />
		</Box>
	);
};
